import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cable, Copy, Eye, EyeOff, KeyRound, LoaderCircle, Plus, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/_shared/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/_shared/components/ui/tabs';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { formatDate } from '@/_shared/lib/format';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract hostname from a redirect URI for display. */
function extractDomain(uri: string): string {
  try {
    return new URL(uri).hostname;
  } catch {
    return uri.length > 30 ? uri.slice(0, 30) + '\u2026' : uri;
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface McpClient {
  clientId: string;
  clientName: string | null;
  redirectUris: string[];
  registeredAt: string;
  userId: string;
  githubLogin: string;
  githubAvatarUrl: string | null;
  lastActiveAt: string;
  scope: string;
}

interface AccessToken {
  tokenHash: string;
  tokenPrefix: string;
  name: string;
  userId: string;
  scope: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export function McpClients(): React.ReactElement {
  usePageTitle('MCP Clients');
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAdmin } = useWorkspace();
  const orpc = useOrpc();

  // --- OAuth clients ---
  const { data, isLoading } = useQuery(orpc.mcpClient.list.queryOptions({ input: {} }));
  const clients: McpClient[] = (data?.clients as McpClient[] | undefined) ?? [];

  const revokeMutation = useMutation(
    orpc.mcpClient.revoke.mutationOptions({
      onSuccess: () => {
        toast.success('Client disconnected');
        void queryClient.invalidateQueries({
          queryKey: orpc.mcpClient.list.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to revoke client'),
    }),
  );

  const handleRevoke = useCallback(
    (clientId: string) => {
      revokeMutation.mutate({ clientId });
    },
    [revokeMutation],
  );

  // --- Access tokens ---
  const { data: tokenData, isLoading: tokensLoading } = useQuery(orpc.mcpAccessToken.list.queryOptions({ input: {} }));
  const tokens: AccessToken[] = (tokenData?.tokens as AccessToken[] | undefined) ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [tokenScope, setTokenScope] = useState<'mcp:read' | 'mcp:read mcp:write'>('mcp:read mcp:write');
  const [newlyCreatedToken, setNewlyCreatedToken] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);

  const createTokenMutation = useMutation(
    orpc.mcpAccessToken.create.mutationOptions({
      onSuccess: (result) => {
        setNewlyCreatedToken(result.token);
        setShowToken(true);
        setTokenName('');
        void queryClient.invalidateQueries({
          queryKey: orpc.mcpAccessToken.list.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to create token'),
    }),
  );

  const revokeTokenMutation = useMutation(
    orpc.mcpAccessToken.revoke.mutationOptions({
      onSuccess: () => {
        toast.success('Access token revoked');
        void queryClient.invalidateQueries({
          queryKey: orpc.mcpAccessToken.list.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to revoke token'),
    }),
  );

  const handleCreateToken = useCallback(() => {
    if (!tokenName.trim()) return;
    createTokenMutation.mutate({ name: tokenName.trim(), scope: tokenScope });
  }, [createTokenMutation, tokenName, tokenScope]);

  const handleRevokeToken = useCallback(
    (tokenHash: string) => {
      revokeTokenMutation.mutate({ tokenHash });
    },
    [revokeTokenMutation],
  );

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setNewlyCreatedToken(null);
    setShowToken(false);
    setTokenName('');
  };

  const mcpUrl = `${window.location.origin}/mcp`;

  const copyUrl = useCallback(() => {
    void navigator.clipboard.writeText(mcpUrl);
    toast.success('MCP URL copied');
  }, [mcpUrl]);

  // Group clients by user
  const grouped = new Map<string, { login: string; avatarUrl: string | null; clients: McpClient[] }>();
  for (const client of clients) {
    const existing = grouped.get(client.userId);
    if (existing) {
      existing.clients.push(client);
    } else {
      grouped.set(client.userId, {
        login: client.githubLogin,
        avatarUrl: client.githubAvatarUrl,
        clients: [client],
      });
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-border/80 bg-white/75 px-6 py-6 backdrop-blur-sm md:px-8">
        <p className="text-[0.6875rem] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          Model Context Protocol
        </p>
        <div className="mt-2">
          <h1 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">MCP</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Connected applications that access your workspace tools via MCP.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 md:px-8 md:py-8">
        <div className="max-w-5xl space-y-8">
          {/* Connected Clients */}
          <section className="rounded border border-border bg-card p-6">
            <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Connected Clients</h2>

            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <LoaderCircle size={24} className="animate-spin" />
              </div>
            ) : clients.length === 0 ? (
              <div className="py-8 text-center">
                <Cable size={32} className="mx-auto mb-3 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">No clients connected</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Follow the setup instructions below to connect Claude.ai, ChatGPT, or other MCP-compatible apps.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {[...grouped.entries()].map(([userId, group]) => (
                  <div key={userId}>
                    {/* User header */}
                    <div className="mb-2 flex items-center gap-2">
                      <Avatar className="size-5">
                        <AvatarImage src={group.avatarUrl ?? undefined} alt={group.login} />
                        <AvatarFallback className="text-[0.5rem]">{group.login[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs font-medium text-muted-foreground">{group.login}</span>
                    </div>

                    {/* Client cards */}
                    <div className="space-y-2">
                      {group.clients.map((client) => {
                        const domain = client.redirectUris[0] ? extractDomain(client.redirectUris[0]) : null;
                        const canDelete = user?.id === client.userId || isAdmin;

                        return (
                          <div
                            key={client.clientId}
                            className="flex items-center gap-3 rounded-md border border-border bg-background px-4 py-3"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-medium text-foreground">
                                  {client.clientName ?? 'Unnamed client'}
                                </p>
                                {domain && (
                                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[0.625rem] text-muted-foreground">
                                    {domain}
                                  </span>
                                )}
                              </div>
                              <div className="mt-0.5 flex items-center gap-3 text-[0.625rem] text-muted-foreground/60">
                                <span>Active {formatDate(client.lastActiveAt)}</span>
                                <span>Registered {new Date(client.registeredAt).toLocaleDateString()}</span>
                                {client.scope.split(' ').map((s) => (
                                  <span key={s} className="rounded bg-muted/50 px-1 py-0.5 font-mono">
                                    {s}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {canDelete && (
                              <button
                                type="button"
                                onClick={() => handleRevoke(client.clientId)}
                                disabled={
                                  revokeMutation.isPending && revokeMutation.variables?.clientId === client.clientId
                                }
                                className="shrink-0 rounded border border-border p-1.5 text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-700 disabled:opacity-30"
                                aria-label={`Disconnect ${client.clientName ?? 'unnamed client'}`}
                                title="Disconnect client"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Access Tokens */}
          <section className="rounded border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold tracking-wider text-muted-foreground uppercase">Access Tokens</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  For programmatic access — CI pipelines, scripts, custom MCP clients.
                </p>
              </div>
              <Dialog open={createOpen} onOpenChange={(open) => (open ? setCreateOpen(true) : handleCloseCreate())}>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    <Plus size={12} />
                    Create Token
                  </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  {newlyCreatedToken ? (
                    <>
                      <DialogHeader>
                        <DialogTitle>Access Token Created</DialogTitle>
                        <DialogDescription>Copy your token now. You won't be able to see it again.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div className="relative">
                          <code className="block rounded border border-border bg-muted/50 px-3 py-2 pr-10 font-mono text-xs break-all text-foreground">
                            {showToken ? newlyCreatedToken : '\u2022'.repeat(40)}
                          </code>
                          <button
                            type="button"
                            onClick={() => setShowToken((p) => !p)}
                            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                            aria-label={showToken ? 'Hide token' : 'Show token'}
                          >
                            {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            void navigator.clipboard.writeText(newlyCreatedToken);
                            toast.success('Token copied');
                          }}
                          className="flex w-full items-center justify-center gap-1.5 rounded border border-border px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary hover:text-primary"
                        >
                          <Copy size={12} />
                          Copy to clipboard
                        </button>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <button
                            type="button"
                            className="rounded bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                          >
                            Done
                          </button>
                        </DialogClose>
                      </DialogFooter>
                    </>
                  ) : (
                    <>
                      <DialogHeader>
                        <DialogTitle>Create Access Token</DialogTitle>
                        <DialogDescription>
                          Generate a token for programmatic access to your workspace MCP tools.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label
                            htmlFor="token-name"
                            className="mb-1.5 block text-xs font-medium text-muted-foreground"
                          >
                            Name
                          </label>
                          <input
                            id="token-name"
                            type="text"
                            value={tokenName}
                            onChange={(e) => setTokenName(e.target.value)}
                            placeholder="e.g. CI pipeline, Cursor"
                            className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Permissions</label>
                          <div className="space-y-2">
                            <label className="flex items-start gap-2 text-sm">
                              <input
                                type="radio"
                                name="scope"
                                value="mcp:read mcp:write"
                                checked={tokenScope === 'mcp:read mcp:write'}
                                onChange={() => setTokenScope('mcp:read mcp:write')}
                                className="mt-0.5"
                              />
                              <span>
                                <span className="font-medium text-foreground">Read &amp; Write</span>
                                <span className="block text-xs text-muted-foreground">
                                  Query warehouses, view dashboards, and save dashboards
                                </span>
                              </span>
                            </label>
                            <label className="flex items-start gap-2 text-sm">
                              <input
                                type="radio"
                                name="scope"
                                value="mcp:read"
                                checked={tokenScope === 'mcp:read'}
                                onChange={() => setTokenScope('mcp:read')}
                                className="mt-0.5"
                              />
                              <span>
                                <span className="font-medium text-foreground">Read only</span>
                                <span className="block text-xs text-muted-foreground">
                                  Query warehouses and view dashboards
                                </span>
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <button
                            type="button"
                            className="rounded border border-border px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                          >
                            Cancel
                          </button>
                        </DialogClose>
                        <button
                          type="button"
                          onClick={handleCreateToken}
                          disabled={!tokenName.trim() || createTokenMutation.isPending}
                          className="rounded bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                          {createTokenMutation.isPending ? 'Creating…' : 'Create'}
                        </button>
                      </DialogFooter>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {tokensLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <LoaderCircle size={24} className="animate-spin" />
              </div>
            ) : tokens.length === 0 ? (
              <div className="py-8 text-center">
                <KeyRound size={32} className="mx-auto mb-3 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">No access tokens</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Create a token for programmatic access to your workspace MCP tools.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {tokens.map((token) => {
                  const canDelete = user?.id === token.userId || isAdmin;
                  return (
                    <div
                      key={token.tokenHash}
                      className="flex items-center gap-3 rounded-md border border-border bg-background px-4 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-medium text-foreground">{token.name}</p>
                          <code className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[0.625rem] text-muted-foreground">
                            {token.tokenPrefix}…
                          </code>
                          {token.scope.split(' ').map((s) => (
                            <span
                              key={s}
                              className="shrink-0 rounded bg-muted/50 px-1 py-0.5 font-mono text-[0.625rem] text-muted-foreground"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                        <div className="mt-0.5 flex items-center gap-3 text-[0.625rem] text-muted-foreground/60">
                          <span>Created {formatDate(token.createdAt)}</span>
                          <span>{token.lastUsedAt ? `Last used ${formatDate(token.lastUsedAt)}` : 'Never used'}</span>
                        </div>
                      </div>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleRevokeToken(token.tokenHash)}
                          disabled={
                            revokeTokenMutation.isPending &&
                            revokeTokenMutation.variables?.tokenHash === token.tokenHash
                          }
                          className="shrink-0 rounded border border-border p-1.5 text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-700 disabled:opacity-30"
                          aria-label={`Revoke ${token.name}`}
                          title="Revoke token"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Setup Instructions */}
          <section className="rounded border border-border bg-card p-6">
            <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">
              Setup Instructions
            </h2>

            {/* MCP Server URL */}
            <div className="mb-6">
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">MCP Server URL</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded border border-border bg-muted/50 px-3 py-2 font-mono text-sm text-foreground">
                  {mcpUrl}
                </code>
                <button
                  type="button"
                  onClick={copyUrl}
                  className="shrink-0 rounded border border-border p-2 text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                  aria-label="Copy MCP URL"
                  title="Copy URL"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            {/* Client-specific instructions via tabs */}
            <Tabs defaultValue="claude-ai">
              <TabsList>
                <TabsTrigger value="claude-ai">Claude.ai</TabsTrigger>
                <TabsTrigger value="chatgpt">ChatGPT</TabsTrigger>
                <TabsTrigger value="claude-code">Claude Code</TabsTrigger>
              </TabsList>

              <TabsContent value="claude-ai">
                <p className="mb-3 text-xs text-muted-foreground/60">Requires Pro, Max, Team, or Enterprise plan</p>
                <ol className="space-y-1.5 text-sm text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">1.</span>
                    Go to{' '}
                    <a
                      href="https://claude.ai/settings/connectors"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-foreground underline decoration-border underline-offset-2 hover:decoration-primary"
                    >
                      Settings &rarr; Connectors
                    </a>{' '}
                    on claude.ai
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">2.</span>
                    Click <strong className="text-foreground">Add custom connector</strong>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">3.</span>
                    Enter a name and paste the MCP Server URL above
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">4.</span>
                    Approve the connection — sign in to Rudder AI if needed
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">5.</span>
                    In a new chat, enable the connector via the <strong className="text-foreground">+</strong> button in
                    the composer
                  </li>
                </ol>
              </TabsContent>

              <TabsContent value="chatgpt">
                <p className="mb-3 text-xs text-muted-foreground/60">Requires Pro or Plus plan with Developer Mode</p>
                <ol className="space-y-1.5 text-sm text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">1.</span>
                    Enable <strong className="text-foreground">Developer Mode</strong> in Settings &rarr; Advanced
                    Settings
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">2.</span>
                    Go to the <strong className="text-foreground">Apps</strong> tab and click{' '}
                    <strong className="text-foreground">Create</strong>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">3.</span>
                    Enter a name and paste the MCP Server URL above
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">4.</span>
                    Approve the connection — sign in to Rudder AI if needed
                  </li>
                </ol>
              </TabsContent>

              <TabsContent value="claude-code">
                <p className="mb-3 text-xs text-muted-foreground/60">Run this command in your terminal</p>
                <div className="rounded border border-border bg-muted/30 px-3 py-2">
                  <code className="text-xs text-foreground">claude mcp add --transport http rudder-ai {mcpUrl}</code>
                </div>
              </TabsContent>
            </Tabs>

            {/* Access token usage callout */}
            <div className="mt-6 rounded border border-border bg-muted/20 px-4 py-3">
              <p className="text-xs font-medium text-foreground">Using an access token?</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                For scripts and CI pipelines, pass your token as a Bearer header:
              </p>
              <div className="mt-2 rounded border border-border bg-muted/30 px-3 py-2">
                <code className="text-xs text-foreground">Authorization: Bearer rai_your_token_here</code>
              </div>
            </div>
          </section>

          {/* Available Tools */}
          <section className="rounded border border-border bg-card p-6">
            <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Available Tools</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Connected clients can use these tools to interact with your workspace.
            </p>
            {/* Keep in sync with src/mcp-external/tools/ */}
            <div className="space-y-4">
              <ToolGroup
                label="Warehouse"
                tools={[
                  { name: 'query_warehouse', description: 'Execute read-only SQL queries' },
                  { name: 'list_tables', description: 'List tables in a warehouse' },
                  { name: 'describe_table', description: 'Describe columns and types of a table' },
                  { name: 'list_warehouses', description: 'Discover available warehouses' },
                ]}
              />
              <ToolGroup
                label="Dashboard"
                tools={[
                  { name: 'list_dashboards', description: 'List workspace dashboards' },
                  { name: 'get_dashboard', description: 'View a dashboard with live data' },
                  { name: 'query_dashboard_panel', description: 'Query a specific dashboard panel' },
                  { name: 'save_dashboard', description: 'Create or update dashboards' },
                ]}
              />
              <ToolGroup
                label="Sources"
                tools={[
                  { name: 'list_source_docs', description: 'List source documentation' },
                  { name: 'get_source_doc', description: 'View source instrumentation docs' },
                ]}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Available Tools
// ---------------------------------------------------------------------------

function ToolGroup({
  label,
  tools,
}: {
  label: string;
  tools: { name: string; description: string }[];
}): React.ReactElement {
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium text-muted-foreground/60">{label}</p>
      <div className="space-y-1">
        {tools.map((t) => (
          <div key={t.name} className="flex items-baseline gap-3 text-sm">
            <code className="shrink-0 text-xs text-foreground">{t.name}</code>
            <span className="text-muted-foreground">{t.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
